import { useAuth0 } from '@auth0/auth0-react';
import * as React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { AddressView } from './views/address/address';
import { AddBoilerHeatingZonesView } from './views/assign-heating-zones/add-boiler-heating-zones';
import { AddHeatPumpHeatingZonesView } from './views/assign-heating-zones/add-heat-pump-heating-zones';
import { ConfigureBoilerHeatingZoneView } from './views/assign-heating-zones/configure-boiler-heating-zone';
import { ConfigureHeatPumpHeatingZoneView } from './views/assign-heating-zones/configure-heat-pump-heating-zone';
import { CheckInstallationView } from './views/checks/check-installation';
import { CheckRouterView } from './views/checks/check-router';
import { ClampView } from './views/current-transformer-meters/clamp';
import { ConfigureCTMeterView } from './views/current-transformer-meters/configuration';
import { DeviceView } from './views/device/device';
import { StickerImageView } from './views/device/sticker-image';
import { AddMetersHeatFlowView } from './views/heat-flow-meter/add-meters';
import { ConfigureHeatFlowMeterView } from './views/heat-flow-meter/configure-meter';
import { HeatingSystemTypeView } from './views/heating-system/heating-system-type';
import { HomeView } from './views/home/home';
import { InstallerGuidesView } from './views/installer-guides/installer-guides';
import { AddInlineMetersView } from './views/inline-meters/add-meters';
import { ConfigureInlineMeterView } from './views/inline-meters/configure-meter';
import { MeterTypeView } from './views/meter-type/meter-type';
import { Shell } from './views/shell';
import { ConfigureTemperatureSensorView } from './views/temperature-sensor/configuration';
import { AddDialView } from './views/thermostat/add-dial';
import { ConfigureDialView } from './views/thermostat/configure-dial';
import { FirmwareCheckView } from './views/verify/firmware/check';
import { FirmwareUpdatedView } from './views/verify/firmware/updated';
import { deviceBasePath, RoutePathsProvider } from '../../shared/context/paths';
import { HotWaterView } from './views/hot-water/hot-water';
import { TestingPeripheralsView } from './views/devices-connected-check/testing-peripherals';
import { CTModelView } from './views/current-transformer-meters/model';
import { CompleteView } from './views/complete/complete';
import { PlotView } from './views/plot/plot';
import { TemperatureModelView } from './views/temperature-sensor/model';

export const routePaths = {
  auth: {
    login: "/login",
    forgotPassword: "/forgot-password",
    verify: "/verify",
  },
  home: { root: "/home" },
  installationGuides: { root: '/installation-guides' },
  check: { root: "/check/installation", checkRouter: "/check/router" },
  device: { root: "/device", sticker: `${deviceBasePath}/sticker` },
  address: { root: `${deviceBasePath}/address` },
  plot: { root: `${deviceBasePath}/plot` },
  meterType: { root: `${deviceBasePath}/meter-type` },
  thermostat: {
    configureDial: `${deviceBasePath}/thermostat/dial/configure`,
    editDial: `${deviceBasePath}/thermostat/dial/configure/:installLocation`,
    addDial: `${deviceBasePath}/thermostat/dial`,
    thermostatModel: `${deviceBasePath}/thermostat/sensor`,
    thermostatConfiguration: `${deviceBasePath}/thermostat/sensor/:deviceType`,
  },
  inlineMeters: {
    root: `${deviceBasePath}/inline-meters`,
    configure: `${deviceBasePath}/inline-meters/configure`,
    edit: `${deviceBasePath}/inline-meters/configure/:deviceLabel`,
  },
  currentTransformerMeters: {
    root: `${deviceBasePath}/current-transformer-meters`,
    model: `${deviceBasePath}/current-transformer-meters/model`,
    clamp: `${deviceBasePath}/current-transformer-meters/clamp`,
    configuration: `${deviceBasePath}/current-transformer-meters/configuration`,
  },
  heatAndFlowMeters: {
    root: `${deviceBasePath}/heat-and-flow-meters`,
    configure: `${deviceBasePath}/heat-and-flow-meters/configure`,
    edit: `${deviceBasePath}/heat-and-flow-meters/:heatFlowMeterCircuit/configure`,
  },
  heatingSystem: {
    root: `${deviceBasePath}/heating-system`,
    assignBoilerHeatingZones: {
      root: `${deviceBasePath}/heating-system/boiler/:boilerType`,
      configure: `${deviceBasePath}/heating-system/boiler/:boilerType/configure`,
      edit: `${deviceBasePath}/heating-system/boiler/:boilerType/configure/:heatingZone`,
    },
    assignHeatPumpHeatingZones: {
      root: `${deviceBasePath}/heating-system/heat-pump/:heatPumpType`,
      configure: `${deviceBasePath}/heating-system/heat-pump/:heatPumpType/configure`,
      edit: `${deviceBasePath}/heating-system/heat-pump/:heatPumpType/configure/:heatingZone`,
    },
  },
  hotWater: {
    root: `${deviceBasePath}/hot-water`,
  },
  verify: {
    firmware: {
      check: `${deviceBasePath}/verify/firmware/check`,
      updated: `${deviceBasePath}/verify/firmware/updated`,
    },
    peripheral: {
      check: `${deviceBasePath}/verify/peripheral/check`,
    },
  },
  complete: {
    root: `${deviceBasePath}/complete`,
  },
};

export const Routes: React.FC = () => {
  const { isLoading, user, isAuthenticated, loginWithRedirect } = useAuth0();

  if (isLoading) {
    return null;
  }

  if (!isLoading && !isAuthenticated) {
    loginWithRedirect({ redirectUri: window.location.origin });
  }

  return (
    <Route
      path="/"
      render={r => (
        <RoutePathsProvider>
          <Shell>
            {user ? (
              <Switch>
                <Route path={routePaths.home.root} component={HomeView} />
                <Route path={routePaths.installationGuides.root} component={InstallerGuidesView} />
                <Route path={routePaths.check.root} component={CheckInstallationView} />
                <Route path={routePaths.check.checkRouter} component={CheckRouterView} />
                <Route exact path={routePaths.device.root} component={DeviceView} />
                <Route path={`${deviceBasePath}`}>
                  <RoutePathsProvider>
                    <Route exact path={routePaths.device.sticker} component={StickerImageView} />
                    <Route exact path={routePaths.address.root} component={AddressView} />
                    <Route exact path={routePaths.plot.root} component={PlotView} />
                    <Route exact path={routePaths.meterType.root} component={MeterTypeView} />
                    <Route exact path={routePaths.inlineMeters.root} component={AddInlineMetersView} />
                    <Route exact path={routePaths.inlineMeters.configure} component={ConfigureInlineMeterView} />
                    <Route exact path={routePaths.inlineMeters.edit} component={ConfigureInlineMeterView} />
                    <Route exact path={routePaths.currentTransformerMeters.model} component={CTModelView} />
                    <Route
                      exact
                      path={routePaths.currentTransformerMeters.configuration}
                      component={ConfigureCTMeterView}
                    />
                    <Route exact path={routePaths.currentTransformerMeters.clamp} component={ClampView} />
                    <Route exact path={routePaths.heatAndFlowMeters.root} component={AddMetersHeatFlowView} />
                    <Route exact path={routePaths.heatAndFlowMeters.configure} component={ConfigureHeatFlowMeterView} />
                    <Route exact path={routePaths.heatAndFlowMeters.edit} component={ConfigureHeatFlowMeterView} />
                    <Route exact path={routePaths.thermostat.addDial} component={AddDialView} />
                    <Route exact path={routePaths.thermostat.configureDial} component={ConfigureDialView} />
                    <Route exact path={routePaths.thermostat.editDial} component={ConfigureDialView} />
                    <Route exact path={routePaths.thermostat.thermostatModel} component={TemperatureModelView} />
                    <Route
                      exact
                      path={routePaths.thermostat.thermostatConfiguration}
                      component={ConfigureTemperatureSensorView}
                    />
                    <Route exact path={routePaths.heatingSystem.root} component={HeatingSystemTypeView} />
                    <Route
                      exact
                      path={routePaths.heatingSystem.assignBoilerHeatingZones.root}
                      component={AddBoilerHeatingZonesView}
                    />
                    <Route
                      exact
                      path={routePaths.heatingSystem.assignBoilerHeatingZones.configure}
                      component={ConfigureBoilerHeatingZoneView}
                    />
                    <Route
                      exact
                      path={routePaths.heatingSystem.assignBoilerHeatingZones.edit}
                      component={ConfigureBoilerHeatingZoneView}
                    />
                    <Route
                      exact
                      path={routePaths.heatingSystem.assignHeatPumpHeatingZones.root}
                      component={AddHeatPumpHeatingZonesView}
                    />
                    <Route
                      exact
                      path={routePaths.heatingSystem.assignHeatPumpHeatingZones.configure}
                      component={ConfigureHeatPumpHeatingZoneView}
                    />
                    <Route
                      exact
                      path={routePaths.heatingSystem.assignHeatPumpHeatingZones.edit}
                      component={ConfigureHeatPumpHeatingZoneView}
                    />
                    <Route exact path={routePaths.hotWater.root} component={HotWaterView} />
                    <Route exact path={routePaths.verify.firmware.check} component={FirmwareCheckView} />
                    <Route exact path={routePaths.verify.firmware.updated} component={FirmwareUpdatedView} />
                    <Route exact path={routePaths.verify.peripheral.check} component={TestingPeripheralsView} />
                    <Route exact path={routePaths.complete.root} component={CompleteView} />
                  </RoutePathsProvider>
                </Route>
                <Route component={HomeView} />
                <Redirect path="*" to={`${r.match.url}home`} />
              </Switch>
            ) : (
              <Switch>
                <Route path={routePaths.auth.login} exact component={HomeView} />
                <Redirect path="*" to={`${r.match.url}login`} />
              </Switch>
            )}
          </Shell>
        </RoutePathsProvider>
      )}
    />
  );
};
